import { useMemo, useRef } from 'react';
import { captureException } from '@sentry/react';
import { isHex } from 'viem';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { getFromList, type Maybe } from '@soundxyz/utils';
import { usePlayerTrackInfo } from '@components/AudioPlayer/usePlayerTrackInfo';
import { useAuth } from '@context/auth/base';
import type { PlaylistInput } from '@graphql/reactQuery';
import {
  CollectorStatusDocument,
  ListeningPartyReleaseInfoDocument,
  useQuery as useGraphQLQuery,
  useReactQuery,
} from '@graphql/reactQuery';
import { usePrefetchAudioOnDelayedHover } from '@hooks/usePrefetchAudioOnDelayedHover';
import { loadTrack, useAudioController } from '@store/audio/AudioController';
import { togglePlayPause } from '@store/audio/AudioEngineHTML5';
import { setPlaylistUpNext } from '@store/audio/AudioFetcher';
import { setActivePlaylist, setPlaybackId } from '@store/audio/AudioMeta';
import {
  releaseListeningPartyMerkleQueryFn,
  releaseListeningPartyMerkleQueryKey,
} from './play/helpers';
import { useTimer } from './useTimer';

/*#__PURE__*/ gql(/* GraphQL */ `
  query CollectorStatus($userId: UUID!, $releaseId: UUID!) {
    user(id: $userId) {
      id
      collectedReleases(filter: { releaseIds: [$releaseId] }) {
        edges {
          node {
            id
          }
        }
      }
    }
  }

  query ListeningPartyReleaseInfo($releaseId: UUID!) {
    release(id: $releaseId) {
      id
      saleSchedules {
        id
        merkleTreeRoot
        startTime
      }
    }
  }
`);

function useHandleListeningParty({
  listeningParty,
  releaseId,
}: {
  listeningParty: Maybe<number>;
  releaseId: Maybe<string>;
}) {
  const { authUserAddress, authUser } = useAuth();

  const userId = authUser?.id;

  const isListeningPartyInTheFuture = listeningParty != null && listeningParty > Date.now();
  const isListeningPartyInThePast = listeningParty != null && listeningParty <= Date.now();

  const { data: isCollector } = useGraphQLQuery(CollectorStatusDocument, {
    variables: !!releaseId &&
      !!userId && {
        userId,
        releaseId,
      },
    staleTime: 0,
    select(data) {
      return !!data.data.user?.collectedReleases.edges.length;
    },
    enabled: isListeningPartyInTheFuture,
  });

  const { data: releasePresale } = useGraphQLQuery(ListeningPartyReleaseInfoDocument, {
    staleTime: 0,
    variables: !!releaseId && { releaseId },
    select(data) {
      return getFromList(
        data.data.release?.saleSchedules,
        v =>
          v.merkleTreeRoot != null &&
          isHex(v.merkleTreeRoot) && {
            merkleTreeRoot: v.merkleTreeRoot,
            startTimeMilliseconds: new Date(v.startTime).getTime(),
          },
      );
    },
    enabled:
      listeningParty != null &&
      authUserAddress != null &&
      isListeningPartyInTheFuture &&
      !isCollector,
  });

  const { data: presaleListeningParty } = useReactQuery({
    queryKey: releaseListeningPartyMerkleQueryKey({
      merkleTreeRoot: releasePresale?.merkleTreeRoot,
      userAddress: authUserAddress,
    }),
    enabled: !!releasePresale && !!authUserAddress && isListeningPartyInTheFuture,
    queryFn() {
      return releaseListeningPartyMerkleQueryFn({
        merkleTreeRoot: releasePresale?.merkleTreeRoot,
        userAddress: authUserAddress,
      });
    },
    select(allowlisted) {
      return allowlisted && releasePresale?.startTimeMilliseconds != null
        ? releasePresale.startTimeMilliseconds
        : null;
    },
  });

  return useMemo((): number | null => {
    if (!listeningParty) return null;

    /**
     * If the listening party is in the past, just ignore
     */
    if (isListeningPartyInThePast) return null;

    /**
     * If authenticated user is collector, we ignore listening parties
     */
    if (isCollector) return null;

    /**
     * If authenticated user is on presale allowlist, and presale starts before public listening party, we ignore public listening party
     */
    if (presaleListeningParty && presaleListeningParty < listeningParty) {
      return presaleListeningParty;
    }

    /**
     * Give the original listening party date
     */
    return listeningParty;
  }, [listeningParty, isCollector, presaleListeningParty, isListeningPartyInThePast]);
}

export function useCurrentListeningParty({
  listeningParty,
  releaseId,

  onListeningPartyStart,
}: {
  listeningParty: Maybe<number>;
  releaseId: Maybe<string>;

  onListeningPartyStart?(): void;
}) {
  const listeningPartyTimestamp = useHandleListeningParty({
    listeningParty,
    releaseId,
  });

  const {
    isRunning: isBeforeListeningParty,
    days,
    hours,
    minutes,
    seconds,
  } = useTimer({
    expiryTimestamp: listeningPartyTimestamp || 1,
    onExpire: onListeningPartyStart,
  });

  return useMemo(() => {
    if (!isBeforeListeningParty || !listeningPartyTimestamp) return null;

    return {
      listeningParty: listeningPartyTimestamp,
      remaining: {
        days,
        hours,
        minutes,
        seconds,
      },
    };
  }, [isBeforeListeningParty, days, hours, minutes, seconds, listeningPartyTimestamp]);
}

export const usePlayButton = ({
  isAlbum,
  trackId,
  releaseId,
  playbackId,
  audioSrc,
  playlist,
  playlistId,
  isSecretPreviewPage = false,
  listeningParty,
  onListeningPartyStart,
}: {
  isAlbum: boolean;
  trackId: string | null;
  releaseId: Maybe<string>;
  listeningParty: Maybe<number>;
  audioSrc?: string;
  playlist?: PlaylistInput | null;
  playbackId?: string;
  playlistId?: string;
  isSecretPreviewPage?: boolean;
  onListeningPartyStart?(): void;
}) => {
  const {
    playing,
    loading: audioLoading,
    ready,
    activeTrackId,
    playbackId: globalPlaybackId,
  } = useAudioController();

  const ref = useRef<HTMLButtonElement>(null);

  usePrefetchAudioOnDelayedHover({ hoverRef: ref, trackId, duration: 200 });

  let isActiveTrack: boolean | undefined;

  if (playbackId) {
    isActiveTrack = playbackId === globalPlaybackId;
  } else {
    isActiveTrack = trackId === activeTrackId;
  }

  const releaseInfo = usePlayerTrackInfo({
    trackId,
    shouldFetch: false,
  });

  const currentListeningParty = useCurrentListeningParty({
    listeningParty,
    releaseId: releaseId ?? releaseInfo.releaseInfo?.id,
    onListeningPartyStart,
  });

  const isDisabled = currentListeningParty ? true : audioSrc ? false : !trackId || isAlbum;

  // In the Draft preview page we don't have access to the track ID yet but we do have an audio source.
  // isActiveTrack will always be false since trackId is undefined, so we should only check if playing
  // is true
  const isPlaying = isSecretPreviewPage ? playing : isActiveTrack && playing;

  const isLoading = isActiveTrack && audioLoading;

  const onPlayClick = useStableCallback(async () => {
    if (isDisabled) return;
    if (isSecretPreviewPage && playing && ready) {
      togglePlayPause();
    } else if (!isActiveTrack && (!!trackId || !!audioSrc)) {
      if (playlistId && trackId) {
        setPlaylistUpNext(playlistId, trackId);
      } else {
        await loadTrack({
          trackId,
          audioSrc,
          playlist,
          addToQueue: !playlistId,
        }).catch(captureException);
      }
      if (playbackId) setPlaybackId(playbackId);
      if (!playlistId) setActivePlaylist(null, []);
    } else if (ready) {
      togglePlayPause();
    }
  });

  return { onPlayClick, ref, isLoading, isPlaying, isDisabled, currentListeningParty };
};

export type CurrentListeningParty = ReturnType<typeof usePlayButton>['currentListeningParty'];
